<template>
  <b-container
    fluid
    class="bg-white"
    :class="{ 'lizard-no-padding': mobileView }">
    <div class="row">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
    <div class="row" :class="{ 'lizard-no-margin': mobileView }">
      <div class="col-sm-1"></div>
      <div class="col-sm-10">
        <div class="lz-toolbar" :class="{ 'lz-toolbar-stacked': mobileView }">
          <!-- NB: deliberately not id="HolidayFilters" / class="allowance-year-group".
               admin/holiday-components/Filters.vue ships an unscoped #HolidayFilters
               block whose ID-level specificity overrides this control's styling. -->
          <div class="lz-year-filter" id="ExpenseYearFilter" v-if="!mobileView">
            <b-form-radio-group
              class="lz-year-toggle"
              v-model="selectedYear"
              :options="years"
              @change="yearChanged"
              button-variant="outline-primary"
              name="expense-years"
              size="sm"
              buttons
              inline
            ></b-form-radio-group>
          </div>
          <b-button
            variant="primary"
            class="lz-trigger-btn"
            size="sm"
            @click="toggleNewActivityModal()">
            <b-icon icon="plus-lg"></b-icon>
            <span>Add New Expense</span>
            <b-icon icon="plus-lg" class="lz-trigger-btn-spacer" aria-hidden="true"></b-icon>
          </b-button>
        </div>
      </div>
      <div class="col-sm-1"></div>
    </div>
    <div class="row" :class="{ 'lizard-no-margin': mobileView }">
      <div class="col-sm-1"></div>
      <div class="col-sm-10">
        <div class="lz-page-card">
          <div v-if="!listItems.length" class="lz-empty-state">
            <b-icon icon="receipt-cutoff" class="lz-empty-icon"></b-icon>
            <p>There aren't any expense records for {{ selectedYear }} yet.</p>
          </div>
          <template v-else>
            <b-table
              caption-top
              responsive
              fixed
              striped
              small
              borderless
              table-class="lz-data-table lz-expense-table"
              variant="secondary"
              :items="listItems"
              :fields="listFields"
              :per-page="Pager.limit"
              :current-page="Pager.page">
              <template #table-caption>Expenses</template>
              <template #cell(projectCode)="data">
                <!-- Same chip as admin/Expenses.vue. Rows are padded with {}
                     placeholders for the pager, hence the v-if. -->
                <div
                  v-if="data.item.project"
                  class="selected-project"
                  :style="
                    'background-color: ' +
                    (data.item.project.color || '#333') +
                    ' !important'
                  ">
                  {{ data.item.project.title }}
                </div>
              </template>
              <template #cell(created)="data">
                {{ formatDate(data.item.created) }}
              </template>
              <template #cell(expenseDate)="data">
                {{ formatDate(data.item.expenseDate) }}
              </template>
              <template #cell(amount)="data">
                {{ calcAmount(data.item.fee, 'amount') }}
              </template>
              <template #cell(vat)="data">
                {{ calcAmount(data.item.fee, 'vat') }}
              </template>
              <template #cell(category)="data">
                {{ categoryLabel(data.item.category) }}
              </template>
              <template #cell(refCode)="data">
                <b-badge
                  class="lz-code-badge"
                  v-if="data.item.refCode"
                  style="cursor: pointer"
                  title="Click to copy"
                  @click="copyRefCode(data.item)">
                  {{ data.item.refCode }}
                </b-badge>
              </template>
              <template #cell(status)="data">
                <b-badge
                  class="lz-status-badge"
                  :variant="GetExpenseStatusVariant(data.item.status)">
                  {{ data.item.status }}
                </b-badge>
              </template>
              <template #cell(notes)="data"
                ><span v-b-popover.hover.top="data.item.notes">{{
                  summarize(data.item.notes)
                }}</span></template
              >
              <template #cell(attachments)="data">
                <ul class="lizard-attachment-list">
                  <li v-if="data.item.attachments">
                    <b-link
                      class="lz-download-link"
                      @click="downloadReceipt(data.item.attachments)">
                      <b-icon icon="download"></b-icon>
                      Download</b-link
                    >
                  </li>
                </ul>
              </template>
              <template #cell(action)="data">
                <b-link
                  @click="doRemove(data.item.uid)"
                  v-if="data.item.status === 'pending'"
                  ><b-icon
                    icon="x-square-fill"
                    variant="danger"
                    title="Remove"></b-icon
                ></b-link>
              </template>
            </b-table>
            <b-pagination
              v-if="Pager.pages > 1"
              v-model="Pager.page"
              :total-rows="Pager.total"
              :per-page="Pager.limit"
              @change="changePage"
              aria-controls="activityTable"
              size="sm"
              pills
              class="lz-pagination"
              align="center"></b-pagination>
          </template>
        </div>
      </div>
      <div class="col-sm-1"></div>
    </div>
    <div class="row" :class="{ 'lizard-no-margin': mobileView }">
      <div class="col-lg lizard-form-empty-col"></div>
    </div>
    <b-overlay no-wrap :show="deleteInProgress">
      <template #overlay>
        <div
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="text-center p-3">
          <p><strong id="form-confirm-label">Are you sure?</strong></p>
          <div class="d-flex">
            <b-button variant="outline-danger" class="mr-3" @click="onCancel">
              Cancel
            </b-button>
            <b-button variant="outline-info" @click="onConfirm"
              >Confirm</b-button
            >
          </div>
        </div>
      </template>
    </b-overlay>
    <b-modal
      size="xl"
      v-model="newActivityModal.show"
      centered
      scrollable
      modal-class="lz-activity-modal"
      content-class="lz-modal-content"
      body-class="lz-modal-body"
      :title="newActivityModal.data.title"
      :header-bg-variant="newActivityModal.headerBgVariant"
      :header-text-variant="newActivityModal.headerTextVariant"
      :body-bg-variant="newActivityModal.bodyBgVariant"
      :body-text-variant="newActivityModal.bodyTextVariant"
      :footer-bg-variant="newActivityModal.footerBgVariant"
      :footer-text-variant="newActivityModal.footerTextVariant">
      <ActivityEntry :activityType="newActivityModal.activityType" />
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="lz-close-btn"
          @click="toggleNewActivityModal()">
          Close
        </b-button>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import ServicesOld from '../../services/index.vue'
import Services from '../../services/main.service.vue'
import ActivityEntry from '../ActivityEntry.vue'
// import FormComponent from './Expenses/Form.vue'
import { format as formatDate } from 'date-fns'

export default {
  name: 'ExpenseLogs',
  props: {
    mobileView: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ActivityEntry
  },
  computed: {
    Store() {
      return this.$store.getters
    },
    Pager() {
      return this.Store.userExpensePager
    },
    listFields() {
      const flds = [
        {
          key: 'created',
          label: 'Submitted Date',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: false
        },
        {
          key: 'projectCode',
          label: 'Project',
          sortable: true,
          thClass: 'w40',
          tdClass: 'w40',
          _showInMobileView: false
        },
        {
          key: 'fee.type',
          label: 'Category',
          sortable: true,
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: false
        },
        {
          key: 'expenseDate',
          label: this.mobileView ? 'Date' : 'Expense Date',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: true
        },
        {
          key: 'amount',
          label: 'Amount',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: true
        },
        {
          key: 'vat',
          label: 'VAT',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: true
        },
        {
          key: 'manager',
          label: 'Manager',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: false
        },
        {
          key: 'attachments',
          label: 'Receipt',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: true
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: true
        },
        {
          key: 'notes',
          label: 'Notes',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: false
        },
        // {
        //   key: 'refCode',
        //   label: 'Reference Code',
        //   thClass: 'w10',
        //   tdClass: 'w10',
        //   _showInMobileView: false
        // },
        {
          key: 'action',
          label: '',
          thClass: 'w10',
          tdClass: 'w10',
          _showInMobileView: false
        }
      ]
      return flds.filter((fld) => {
        return this.mobileView ? fld._showInMobileView : true
      })
    }
  },
  data() {
    const currentYear = new Date().getFullYear()
    return {
      // Expenses are claimed after the fact, so the switcher looks back a year
      // rather than forward like the leave one.
      selectedYear: currentYear,
      years: [currentYear - 1, currentYear],
      viewType: 'list',
      deleteInProgress: false,
      deleteItemId: null,
      uploading: false,
      listItems: [],
      calendarItems: {},
      calDate: '',
      context: null,
      categories: {},
      gbpRates: { eur: 0, usd: 0 },
      newActivityModal: {
        show: false,
        activityType: 'exp',
        headerBgVariant: 'white',
        headerTextVariant: 'dark',
        bodyBgVariant: 'white',
        bodyTextVariant: 'dark',
        footerBgVariant: 'white',
        footerTextVariant: 'dark',
        data: {
          selectedDate: null,
          title: 'New Expense',
          listFields: [
            {
              key: 'name',
              label: 'Name',
              sortable: true
            },
            {
              key: 'hours',
              label: 'Hours',
              sortable: true,
              thClass: 'lizard-column-hours',
              tdClass: 'lizard-column-hours'
            }
          ],
          listItems: [],
          perPage: 10,
          currentPage: 1
        }
      }
    }
  },
  mounted() {
    this.$root.$on('parent_tab_expenses_selected', this.tabMounted)
    this.$root.$on('saved', this.tabMounted)
    this.tabMounted()
  },
  methods: {
    async tabMounted() {
      this.gbpRates = this.$cookies.get('__gbp-rates') || this.gbpRates
      await this.GetCategories()
      const now = new Date()
      this.calDate = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      this.GetList()
    },
    changePage(page) {
      this.$store.commit('setUserExpensePager', { page })
    },
    yearChanged(year) {
      this.selectedYear = year
      // Page 1 of the new year: if the page actually changes the Pager watcher
      // refetches, otherwise fetch here so we don't do it twice.
      if ((this.Pager?.page ?? 1) !== 1) {
        this.$store.commit('setUserExpensePager', { page: 1 })
        return
      }
      this.GetList()
    },
    async toggleNewActivityModal() {
      try {
        // check connection
        await Services.Users.fetchCurrentUser()
        this.newActivityModal.show = !this.newActivityModal.show
      } catch (Exception) {
        console.error(Exception)
        // this.$root.$emit('user-signed-out')
      }
    },
    async GetList() {
      try {
        let { status, data } = await ServicesOld.GetExpenses({
          page: this.Pager?.page ?? 1,
          year: this.selectedYear
        })
        if (status === 200) {
          this.$store.commit('setUserExpensePager', data.content.info)
          const structurizeData = this.structurizeDataSchema(data.content.list)
          const startIndex =
            (data.content.info.page - 1) * data.content.info.limit
          let listItr = -1
          this.listItems = new Array(data.content.info.total)
            .fill({})
            .map((itm, idx) => {
              if (idx >= startIndex && idx < data.content.info.total) {
                listItr++
                return structurizeData[listItr] ?? {}
              }
              return {}
            })
        } else {
          return []
        }
      } catch (Exception) {
        console.log('GetList.cacheError', Exception)
      }
    },
    async GetCategories() {
      const { status, data } = await ServicesOld.GetExpenseCategories()
      if (status === 200) {
        data.content.forEach(({ value, name }) => {
          this.categories[value] = name
        })
      }
    },
    structurizeDataSchema(data = []) {
      data = data
        .map((itm) => {
          itm.project = itm.project || { title: 'CANDESIC' }
          this.calendarItems[itm.date] = itm.amount
          itm.fee.type = itm.fee.type.replace(/_/g, ' ').toUpperCase()
          itm.manager = `${itm?.manager?.firstName ?? ''} ${
            itm?.manager?.lastName ?? ''
          }`.trim()
          itm.action = {
            id: itm.uid
          }
          return itm
        })
        .sort((a, b) => {
          if (a.created > b.created) {
            return -1
          } else if (a.created < b.created) {
            return 1
          } else {
            return 0
          }
        })
      return data
    },
    currencySymbol(currency) {
      let result = ''
      switch (currency.toLowerCase()) {
        case 'usd':
          result = '$'
          break
        case 'eur':
          result = '€'
          break
        default:
          result = '£'
      }
      return result
    },
    categoryLabel(cid) {
      return this.categories[cid] ?? 'n/a'
    },
    calcAmount(itm, amountKey) {
      let toGBP = ''
      return (
        this.currencySymbol(itm.currency) +
        parseFloat(itm[amountKey]).toFixed(2) +
        toGBP
      )
    },
    async downloadReceipt(fileName) {
      if (fileName === null) {
        return
      }
      ServicesOld.DownloadExpenseBill(fileName)
    },
    async removeBill(itm) {
      itm.attachments = null
    },
    async uploadBill(ev) {
      this.uploading = true
      const { data, status } = await ServicesOld.UploadExpenseBill(
        ev.target.files[0]
      )
      if (status === 200) {
        const itmId = ev.target.attributes['itmId'].value
        let itm = {}
        this.listItems.forEach((listItm) => {
          if (itmId === listItm.uid) {
            itm = listItm
          }
        })
        delete itm.action
        itm.attachments = data.fileName
        await ServicesOld.UpdateExpense(itmId, {
          attachmentKeys: itm.attachments
        })
        this.uploading = false
      } else {
        this.uploading = false
      }
    },
    async doRemove(id) {
      this.deleteInProgress = true
      this.deleteItemId = id
    },
    onCancel() {
      this.deleteInProgress = false
      this.deleteItemId = null
    },
    async onConfirm() {
      this.deleteInProgress = false
      if (!this.deleteItemId) {
        return
      }
      let { status } = await ServicesOld.RemoveExpense(this.deleteItemId)
      if (status === 200) {
        this.listItems = this.listItems.filter((itm) => {
          return itm.uid !== this.deleteItemId
        })
      }
      this.deleteItemId = null
      this.deleteInProgress = false
    },
    onContext(ctx) {
      this.context = ctx
    },
    toggleViewType() {
      this.viewType = this.viewType === 'list' ? 'calendar' : 'list'
    },
    dateClass(ymd, date) {
      let className = ''
      const dt = this.$moment(date).format('YYYY-MM-DD')
      if (this.calendarItems[dt]) {
        className = 'half-activity-calday'
      }
      return this.$moment().diff(dt) > 0 ? className : ''
    },
    GetExpenseStatusVariant(status) {
      let variant = 'light'
      switch (status.toLowerCase()) {
        case 'approved':
          variant = 'info'
          break
        case 'rejected':
          variant = 'danger'
          break
        case 'pending':
          variant = 'warning'
          break
        case 'paid':
          variant = 'success'
          break
        case 'failed':
          variant = 'dark'
          break
      }
      return variant
    },
    formatDate(dt) {
      return formatDate(new Date(dt), 'dd/MM/yyyy')
    },
    summarize(note) {
      if (!note) {
        return ''
      }
      note = note.trim()
      if (note.length > 20) {
        return note.substring(0, 20) + '...'
      } else {
        return note
      }
    },
    copyRefCode({ refCode = null }) {
      if (refCode === null) {
        return
      }
      navigator.clipboard.writeText(refCode).then(
        () => {
          this.$toasted.show('Reference code copied to clipboard', {
            type: 'info',
            duration: 3000
          })
        },
        function (err) {
          console.error('Could not copy text: ', err)
        }
      )
    }
  },
  watch: {
    Pager: {
      handler(nxt, prv) {
        const prevPage = prv?.page ?? 1
        const nextPage = nxt?.page ?? 1
        if (prevPage !== nextPage) {
          this.GetList()
        }
      },
      deep: true
    }
  }
}
</script>

<style>
.half-activity-calday {
  background-color: #ccc !important;
  color: #333 !important;
}
.lizard-text-center {
  text-align: center !important;
}
.lizard-text-center.white {
  color: #fff !important;
}
.lizard-col-w5 {
  width: 5% !important;
}
.lizard-col-w10 {
  width: 10% !important;
}
.lizard-col-w15 {
  width: 15% !important;
}
.lizard-col-w20 {
  width: 20% !important;
}
.lizard-col-w25 {
  width: 25% !important;
}
.lizard-col-w30 {
  width: 30% !important;
}
.lizard-activity-list-action a {
  color: #f00;
}
.lizard-activity-list-action a:hover {
  color: #c00;
}
.lizard-form-empty-col {
  min-height: 20px !important;
}
.lizard-view-link {
  font-weight: bold;
}
/* Modal styling lives in src/styles/logs.css. */
.lizard-today-btn {
  margin: 10px;
  float: right;
}
.lizard-attachment-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.lizard-attachment-list li {
  display: inline-block;
  margin: 0 5px;
}
.lizard-remove-link {
  color: #c00;
  font-variant: small-caps;
  font-size: 10pt;
  font-weight: bold;
}
.lizard-remove-link:hover {
  color: #600;
  text-decoration: underline;
}
</style>
